/**
 * The remote guide stream has one audible route. Narration is retained as PCM
 * in that route, so an empty microphone interruption can resume the original
 * audio rather than asking the provider to speak the passage again.
 */
export type BufferedNarrationPlayback = {
  start: (owner: string, options?: { source?: 'stream' | 'chunks' }) => void;
  /** True means queued for this owner, rather than accepted or heard. */
  append: (owner: string, pcm: Float32Array) => boolean;
  providerStarted: (owner: string) => void;
  pause: (owner: string) => boolean;
  resume: (owner: string) => boolean;
  providerEnded: (owner: string) => void;
  /** Silence/discard old PCM while the caller clears its provider response. */
  discard: (owner: string) => void;
  /** Release only at the next verified ordinary provider output boundary. */
  releasePassthrough: (discardedOwner: string) => void;
  setVolume: (volume: number, muted: boolean) => void;
  heardMs: (owner: string) => number;
  close: () => Promise<void>;
  isRunning: () => boolean;
};

export type BufferedNarrationPlaybackOptions = {
  onStarted: (owner: string) => void;
  onDrained: (owner: string, result: { playedDurationMs: number }) => void;
  onError: (error: Error, owner?: string) => void;
  signal?: AbortSignal;
  workletUrl?: string;
  maxBufferedSeconds?: number;
  tailGuardMs?: number;
  silenceSettleMs?: number;
  sampleRate?: number;
};

type PlaybackMessage = {
  type?: string;
  owner?: string;
  playedDurationMs?: number;
  message?: string;
};

const STARTUP_TIMEOUT_MS = 5000;
const MAX_BUFFERED_SECONDS = 120;
const MAX_ENCODED_PCM_LENGTH = 1024 * 1024;
const NARRATION_PCM_SAMPLE_RATE = 24000;

/** Decode provider PCM16LE without retaining or logging its encoded payload. */
export const decodeNarrationPcm16 = (encoded: string): Float32Array => {
  if (typeof encoded !== 'string' || encoded.length === 0 || encoded.length > MAX_ENCODED_PCM_LENGTH
    || encoded.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(encoded)) {
    throw new Error('Voice narration contained invalid audio data. Please reconnect.');
  }
  let bytes: string;
  try { bytes = atob(encoded); } catch {
    throw new Error('Voice narration contained invalid audio data. Please reconnect.');
  }
  if (bytes.length === 0 || bytes.length % 2 !== 0) {
    throw new Error('Voice narration contained incomplete audio data. Please reconnect.');
  }
  const samples = new Float32Array(bytes.length / 2);
  for (let i = 0; i < samples.length; i += 1) {
    const value = bytes.charCodeAt(i * 2) | (bytes.charCodeAt(i * 2 + 1) << 8);
    samples[i] = (value >= 0x8000 ? value - 0x10000 : value) / 0x8000;
  }
  return samples;
};

export const createBufferedNarrationPlayback = async (
  stream: MediaStream,
  options: BufferedNarrationPlaybackOptions,
): Promise<BufferedNarrationPlayback> => {
  if (typeof AudioContext === 'undefined' || typeof AudioWorkletNode === 'undefined') {
    throw new Error('Voice narration requires AudioWorklet audio playback in this browser. Please use a supported browser.');
  }
  if (options.sampleRate !== undefined && (!Number.isFinite(options.sampleRate) || options.sampleRate <= 0)) {
    throw new Error('Voice narration requires a valid audio sample rate.');
  }
  const context = options.sampleRate === undefined ? new AudioContext() : new AudioContext({ sampleRate: options.sampleRate });
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioWorkletNode | null = null;
  let decoderClock: HTMLAudioElement | null = null;
  let closed = false;
  let ready = false;
  let owner: string | null = null;
  let quarantinedOwner: string | null = null;
  let sourceMode: 'stream' | 'chunks' = 'stream';
  let chunkEnded = false;
  let heardDurationMs = 0;
  let started = false;
  const retiredOwners = new Set<string>();
  let closePromise: Promise<void> | null = null;
  let rejectStartup: ((error: Error) => void) | null = null;

  const close = (): Promise<void> => {
    if (closePromise) return closePromise;
    closed = true;
    ready = false;
    options.signal?.removeEventListener('abort', abort);
    context.onstatechange = null;
    if (decoderClock) {
      decoderClock.onerror = null;
      decoderClock.pause();
      decoderClock.srcObject = null;
    }
    if (node) {
      node.onprocessorerror = null;
      node.port.onmessage = null;
      try { node.port.postMessage({ type: 'close' }); } catch { /* Already closed. */ }
      node.port.close();
      node.disconnect();
    }
    source?.disconnect();
    owner = null;
    quarantinedOwner = null;
    heardDurationMs = 0;
    closePromise = context.close().catch(() => undefined);
    return closePromise;
  };
  const abort = () => {
    rejectStartup?.(new Error('Voice narration playback was cancelled.'));
    void close();
  };
  const fail = (error: Error) => {
    if (closed) return;
    const failedOwner = owner ?? undefined;
    rejectStartup?.(error);
    void close();
    options.onError(error, failedOwner);
  };
  const waitForReady = async (operation: Promise<void>) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        operation,
        new Promise<never>((_, reject) => {
          rejectStartup = reject;
          timer = setTimeout(() => reject(new Error('Voice narration audio did not start. Please reconnect.')), STARTUP_TIMEOUT_MS);
        }),
      ]);
      if (closed || options.signal?.aborted) throw new Error('Voice narration playback was cancelled.');
    } finally {
      rejectStartup = null;
      if (timer !== undefined) clearTimeout(timer);
    }
  };
  const post = (type: string, target: string) => {
    if (!closed && target === owner) node?.port.postMessage({ type, owner: target });
  };

  try {
    if (options.signal?.aborted) throw new Error('Voice narration playback was cancelled.');
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.sampleRate !== undefined && context.sampleRate !== options.sampleRate) {
      throw new Error('This browser cannot play narration at the required audio sample rate. Please use a supported browser.');
    }
    if (!context.audioWorklet?.addModule) throw new Error('This browser cannot buffer voice narration audio.');
    await waitForReady(context.audioWorklet.addModule(
      options.workletUrl ?? `${process.env.PUBLIC_URL || ''}/voice-narration-playback-worklet.js`,
    ));
    // Chromium can receive WebRTC packets without pulling decoded PCM into
    // Web Audio until a media renderer plays that same remote stream. Keep a
    // separate detached renderer permanently muted; only the worklet is audible.
    // https://issues.chromium.org/issues/40094084
    decoderClock = new Audio();
    decoderClock.muted = true;
    decoderClock.defaultMuted = true;
    decoderClock.srcObject = stream;
    decoderClock.onerror = () => fail(new Error('Voice narration audio decoding stopped. Please reconnect.'));
    source = context.createMediaStreamSource(stream);
    node = new AudioWorkletNode(context, 'voice-narration-playback', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: {
        maxBufferedSeconds: options.maxBufferedSeconds ?? 120,
        tailGuardMs: options.tailGuardMs ?? 300,
        silenceSettleMs: options.silenceSettleMs ?? 300,
      },
    });
    node.port.onmessage = ({ data }: MessageEvent<PlaybackMessage>) => {
      if (closed || !data || data.owner !== owner) return;
      if (data.type === 'error') {
        fail(new Error(data.message || 'Voice narration playback stopped. Please reconnect.'));
        return;
      }
      if (typeof data.playedDurationMs === 'number' && Number.isFinite(data.playedDurationMs)) {
        heardDurationMs = Math.max(heardDurationMs, Math.max(0, data.playedDurationMs));
      }
      if (data.type === 'started' && !started && owner) {
        started = true;
        options.onStarted(owner);
      } else if (data.type === 'drained' && owner && started) {
        const drainedOwner = owner;
        const playedDurationMs = heardDurationMs;
        retiredOwners.add(drainedOwner);
        owner = null;
        options.onDrained(drainedOwner, { playedDurationMs });
      }
    };
    node.onprocessorerror = () => fail(new Error('Voice narration audio stopped unexpectedly. Please reconnect.'));
    context.onstatechange = () => {
      if (ready && context.state !== 'running') {
        fail(new Error('Voice narration audio was interrupted by the browser. Please reconnect.'));
      }
    };
    source.connect(node);
    node.connect(context.destination);
    await waitForReady(context.resume());
    await waitForReady(decoderClock.play());
    if (context.state !== 'running') throw new Error('Voice narration audio is blocked. Please enable playback and reconnect.');
    ready = true;

    return {
      start: (nextOwner, startOptions) => {
        if (closed || !nextOwner || nextOwner === owner || retiredOwners.has(nextOwner)) return;
        const requestedSource = startOptions?.source ?? 'stream';
        if (requestedSource !== 'stream' && requestedSource !== 'chunks') {
          fail(new Error('Voice narration requested an invalid audio source. Please reconnect.'));
          return;
        }
        if (requestedSource === 'chunks' && context.sampleRate !== NARRATION_PCM_SAMPLE_RATE) {
          fail(new Error('Voice narration chunks require 24 kHz audio playback. Please reconnect.'));
          return;
        }
        if (owner) retiredOwners.add(owner);
        owner = nextOwner;
        quarantinedOwner = null;
        sourceMode = requestedSource;
        chunkEnded = false;
        heardDurationMs = 0;
        started = false;
        node?.port.postMessage({ type: 'start', owner: nextOwner, source: requestedSource });
      },
      append: (target, pcm) => {
        if (closed || target !== owner || sourceMode !== 'chunks' || chunkEnded) return false;
        if (!(pcm instanceof Float32Array) || pcm.length === 0
          || pcm.length > context.sampleRate * MAX_BUFFERED_SECONDS
          || pcm.some((sample) => !Number.isFinite(sample) || Math.abs(sample) > 1)) {
          fail(new Error('Voice narration contained invalid or oversized audio samples. Please reconnect.'));
          return false;
        }
        // The caller may retain its decoded chunk; transfer only our own copy.
        const copiedBuffer = new ArrayBuffer(pcm.byteLength);
        const samples = new Float32Array(copiedBuffer);
        samples.set(pcm);
        try {
          node?.port.postMessage({ type: 'append', owner: target, samples }, [copiedBuffer]);
          return true;
        } catch {
          fail(new Error('Voice narration audio could not be queued. Please reconnect.'));
          return false;
        }
      },
      providerStarted: (target) => post('provider_started', target),
      pause: (target) => {
        if (closed || target !== owner) return false;
        post('pause', target);
        return true;
      },
      resume: (target) => {
        if (closed || target !== owner) return false;
        post('resume', target);
        return true;
      },
      providerEnded: (target) => {
        if (target !== owner || closed) return;
        if (sourceMode === 'chunks') chunkEnded = true;
        post('provider_ended', target);
      },
      discard: (target) => {
        if (closed || target !== owner) return;
        post('discard', target);
        retiredOwners.add(target);
        quarantinedOwner = target;
        owner = null;
        heardDurationMs = 0;
        started = false;
      },
      releasePassthrough: (target) => {
        if (closed || owner || target !== quarantinedOwner) return;
        node?.port.postMessage({ type: 'release_passthrough', owner: target });
        quarantinedOwner = null;
      },
      setVolume: (volume, muted) => {
        if (!closed) node?.port.postMessage({
          type: 'volume',
          volume: Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0,
          muted: Boolean(muted),
        });
      },
      heardMs: (target) => target === owner ? heardDurationMs : 0,
      close,
      isRunning: () => !closed && ready && context.state === 'running',
    };
  } catch (error) {
    await close();
    throw error;
  }
};
